
import { UseFormRegister, UseFormWatch, UseFormSetValue } from 'react-hook-form';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import type { Application } from '@/types/database.types';
import { useEffect } from 'react';

interface AncillaryCarrierInformationProps {
  register: UseFormRegister<Partial<Application>>;
  watch: UseFormWatch<Partial<Application>>;
  setValue: UseFormSetValue<Partial<Application>>;
  data: Partial<Application>;
}

export const AncillaryCarrierInformation = ({
  register,
  watch,
  setValue,
  data
}: AncillaryCarrierInformationProps) => {
  
  useEffect(() => {
    // Initialize values on component mount only
    if (data) {
      // Handle current_ancillary_carrier initialization
      setValue('current_ancillary_carrier', data.current_ancillary_carrier === null ? '' : data.current_ancillary_carrier || '');
      
      // For arrays like desired carriers - ensure it's initialized as empty array not null
      if (!data.desired_ancillary_carriers) {
        setValue('desired_ancillary_carriers', []);
      } else if (typeof data.desired_ancillary_carriers === 'string') {
        try {
          // Try to parse if it's a JSON string
          const parsed = JSON.parse(data.desired_ancillary_carriers as string);
          setValue('desired_ancillary_carriers', Array.isArray(parsed) ? parsed : [data.desired_ancillary_carriers]);
        } catch (e) {
          // If parsing fails, treat it as a single value
          setValue('desired_ancillary_carriers', [data.desired_ancillary_carriers]);
        }
      }
    }
  }, [data, setValue]);
  
  const handleDesiredCarrierChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Convert comma-separated string to array
    const carrierString = e.target.value || '';
    const carriers = carrierString.split(',').map(c => c.trim()).filter(Boolean);
    setValue('desired_ancillary_carriers', carriers);
  };

  const formatCarriersForDisplay = (carriers: string[] | string | null | undefined): string => {
    if (!carriers) return '';
    if (typeof carriers === 'string') {
      if (carriers.startsWith('[')) {
        try {
          const parsedCarriers = JSON.parse(carriers);
          return Array.isArray(parsedCarriers) ? parsedCarriers.join(', ') : '';
        } catch (e) {
          return carriers;
        }
      }
      return carriers;
    }
    return Array.isArray(carriers) ? carriers.join(', ') : '';
  };
  
  // Watch current values for display
  const desiredAncillaryCarriers = watch('desired_ancillary_carriers');
  
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="current_ancillary_carrier">Current Ancillary Benefits Carrier</Label>
        <Input
          id="current_ancillary_carrier"
          placeholder="Enter current ancillary benefits carrier"
          {...register('current_ancillary_carrier')}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="desired_ancillary_carriers">Desired Ancillary Carriers (comma separated)</Label>
        <Input
          id="desired_ancillary_carriers"
          value={formatCarriersForDisplay(desiredAncillaryCarriers)}
          onChange={handleDesiredCarrierChange}
          placeholder="Guardian, MetLife, etc."
        />
      </div>
    </div>
  );
};
